import { useState, useEffect, useRef, useCallback } from "react";
import { DEFAULT_ROW_COUNT, API_PAGE_SIZE } from "../constants/tableRowsCounts";
import { fetchUserPreferences, updateRecordsPerPage } from "../api/user";

// App-specific filter shapes and PrimeReact's DataTableFilterMeta both flow
// through the hook; neither satisfies the other's typing (PrimeReact requires
// matchMode, app filter interfaces lack index signatures), so keep it `object`
export type LazyFilterMeta = object;

export interface LazyTableState {
  first: number;
  rows: number;
  // PrimeReact sort/filter events don't carry `page`, so it can be absent
  // after the state is replaced by one of those events
  page?: number;
  sortField?: string | null;
  sortOrder?: number | null;
  filters?: LazyFilterMeta;
}

// Page/sort/filter events coming from a DataTable or synthesized by consumers
export type LazyStateEvent = LazyTableState & { [key: string]: unknown };

export interface LoadDataParams {
  page: number;
  perPage: number;
  sortField?: string | null;
  sortOrder?: number | null;
  filters?: LazyFilterMeta;
}

export interface UseApiPaginationOptions<TResponse extends object> {
  loadData: (params: LoadDataParams) => Promise<TResponse>;
  /**
   * Called on **every** page fetch — not only the last page.
   *
   * The hook calls this after each individual API page is loaded to keep
   * `totalRecords` up-to-date as pages arrive.  For non-last pages the
   * returned value is used immediately but will be overwritten when the last
   * page is fetched, so implementations must be correct for any page, not
   * just the final one.
   *
   * A safe pattern when the API returns a `totalPages` / `pagesNumber` field:
   *   `(totalPages - 1) * pageSize + currentPageItems.length`
   * This formula yields the exact total regardless of which page is supplied.
   */
  calculateTotalRecords: (response: TResponse, pageSize: number) => number;
  storageKey: string;
  dataKey?: string;
}

export const useApiPagination = <TResponse extends object>({
  loadData,
  calculateTotalRecords,
  storageKey,
  dataKey = "data",
}: UseApiPaginationOptions<TResponse>) => {
  // Track if component is mounted
  const mountedRef = useRef(false);

  // Stable refs for consumer-provided callbacks so they can be used inside
  // memoized functions without triggering unnecessary re-renders or dep cycles.
  const loadDataRef = useRef(loadData);
  const calculateTotalRecordsRef = useRef(calculateTotalRecords);
  useEffect(() => {
    loadDataRef.current = loadData;
    calculateTotalRecordsRef.current = calculateTotalRecords;
  });

  // API page size - synced with user's recordsPerPage preference.
  //
  // NOTE: Before `fetchUserPreferences` resolves, `apiPageSize` starts at
  // `API_PAGE_SIZE` (100) while `lazyState.rows` starts at `DEFAULT_ROW_COUNT`
  // (10).  This intentional divergence is harmless: the `!preferencesLoaded`
  // guard in the data-loading effect blocks all fetches until both values have
  // been aligned to the user's saved preference, so no incorrect request is
  // ever fired and the UI shows skeleton rows during that window.
  const [apiPageSize, setApiPageSize] = useState(API_PAGE_SIZE);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Read persisted DataTable state
  const getPersistedState = (key: string): Partial<LazyTableState> | null => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const persistedState = getPersistedState(storageKey);

  // Data state
  const [dataCache, setDataCache] = useState<Record<number, unknown[]>>({});
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  // Lazy state - from persisted state with fallbacks
  const [lazyState, setLazyState] = useState<LazyTableState>(() => {
    return {
      first: persistedState?.first || 0,
      rows: persistedState?.rows || DEFAULT_ROW_COUNT,
      page: Math.max(persistedState?.page || 1, 1),
      sortField: persistedState?.sortField || null,
      sortOrder: persistedState?.sortOrder || null,
      filters: persistedState?.filters || {},
    };
  });

  // Persist state to localStorage
  const persistState = (state: LazyTableState) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.warn("Failed to persist state to localStorage:", error);
    }
  };

  // Fetch user preferences on mount
  useEffect(() => {
    mountedRef.current = true;

    fetchUserPreferences()
      .then((prefs: { recordsPerPage?: number } | undefined) => {
        if (mountedRef.current) {
          const recordsPerPage = prefs?.recordsPerPage || API_PAGE_SIZE;
          setApiPageSize(recordsPerPage);
          // Sync lazyState.rows with user preference
          setLazyState(prev => ({
            ...prev,
            rows: recordsPerPage,
          }));
          setPreferencesLoaded(true);
        }
      })
      .catch((error: unknown) => {
        console.warn("Failed to fetch user preferences:", error);
        if (mountedRef.current) {
          setPreferencesLoaded(true);
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Update persisted state when lazyState changes
  useEffect(() => {
    persistState(lazyState);
  }, [lazyState]);

  // Get data slice from cache
  const getVisibleData = () => {
    if (loading || !preferencesLoaded) {
      // Return placeholder rows for skeleton rendering
      return Array(lazyState.rows).fill(null);
    }

    // Calculate the range of API pages we need
    const firstPageNeeded = Math.floor(lazyState.first / apiPageSize) + 1;
    const lastRecordIndex = lazyState.first + apiPageSize - 1;
    const lastPageNeeded = Math.floor(lastRecordIndex / apiPageSize) + 1;

    // Check if we have all pages cached
    let hasAllPages = true;
    for (let page = firstPageNeeded; page <= lastPageNeeded; page++) {
      if (!dataCache[page]) {
        hasAllPages = false;
        break;
      }
    }

    // If we don't have all pages, return placeholder rows for skeleton rendering
    if (!hasAllPages) {
      return Array(lazyState.rows).fill(null);
    }

    // Combine data from all pages
    let combinedData: unknown[] = [];
    for (let page = firstPageNeeded; page <= lastPageNeeded; page++) {
      combinedData = combinedData.concat(dataCache[page] || []);
    }

    // Calculate correct start and end positions in the combined data
    const start = lazyState.first % apiPageSize;
    const end = start + apiPageSize;

    // Make sure we don't exceed the data length
    const slicedData = combinedData.slice(start, end);

    return slicedData;
  };

  // Keep track of pages that are being loaded to prevent duplicate requests
  const loadingPagesRef = useRef(new Set<number>());

  // Load data chunk
  // sortField / sortOrder / filters are passed explicitly so this callback
  // never captures stale lazyState values regardless of when it was created.
  const loadDataChunk = useCallback(
    async (
      apiPage: number,
      sortField: string | null | undefined,
      sortOrder: number | null | undefined,
      filters: LazyFilterMeta | undefined
    ) => {
      // Check if chunk is cached
      if (dataCache[apiPage]) {
        return;
      }

      // Check if page is already being loaded
      if (loadingPagesRef.current.has(apiPage)) {
        return;
      }

      // Only set loading if component is still mounted
      if (mountedRef.current) {
        setLoading(true);
      }

      // Mark page as being loaded
      loadingPagesRef.current.add(apiPage);

      try {
        const response = await loadDataRef.current({
          page: apiPage,
          perPage: apiPageSize,
          sortField,
          sortOrder,
          filters,
        });

        if (mountedRef.current) {
          // Calculate totalRecords with the current apiPageSize
          const total = calculateTotalRecordsRef.current(response, apiPageSize);
          setTotalRecords(total);

          // Cache the chunk
          setDataCache(prev => ({
            ...prev,
            [apiPage]: (response as Record<string, unknown[] | undefined>)[dataKey] || [],
          }));
        }
      } catch (error) {
        // Only log error if component is still mounted
        if (mountedRef.current) {
          console.error("Failed to load data:", error);
        }
        throw error;
      } finally {
        // Remove from loading pages
        loadingPagesRef.current.delete(apiPage);

        // Only set loading to false if component is still mounted and no other pages are loading
        if (mountedRef.current && loadingPagesRef.current.size === 0) {
          setLoading(false);
        }
      }
    },
    // loadDataRef and calculateTotalRecordsRef are stable refs (updated via useEffect on every
    // render) so they are intentionally excluded from the dependency array.
    [dataCache, apiPageSize, dataKey]
  );

  // Load lazy data
  const loadLazyData = useCallback(async () => {
    // Calculate which API pages we need to load based on the current view
    const firstPageNeeded = Math.floor(lazyState.first / apiPageSize) + 1;
    const lastRecordIndex = lazyState.first + apiPageSize - 1;
    const lastPageNeeded = Math.floor(lastRecordIndex / apiPageSize) + 1;

    // Calculate total possible pages based on totalRecords
    const totalPages = Math.ceil(totalRecords / apiPageSize);

    // Adjust lastPageNeeded to not exceed totalPages if we have totalRecords
    const adjustedLastPageNeeded =
      totalRecords > 0 && totalPages > 0 ? Math.min(lastPageNeeded, totalPages) : lastPageNeeded;

    // Check which pages still need to be loaded
    // Don't load pages that exceed the total pages count
    const pagesToLoad: number[] = [];
    for (let page = firstPageNeeded; page <= adjustedLastPageNeeded; page++) {
      if (!dataCache[page] && !loadingPagesRef.current.has(page)) {
        pagesToLoad.push(page);
      }
    }

    // If no pages need to be loaded, return
    if (pagesToLoad.length === 0) {
      return;
    }

    // Pass sort/filter values explicitly so each chunk call reads the values
    // that were current when loadLazyData was created, not a stale closure.
    const { sortField, sortOrder, filters } = lazyState;
    const loadPromises = pagesToLoad.map(page =>
      loadDataChunk(page, sortField, sortOrder, filters)
    );

    await Promise.all(loadPromises);
  }, [
    lazyState.first,
    lazyState.sortField,
    lazyState.sortOrder,
    lazyState.filters,
    apiPageSize,
    totalRecords,
    dataCache,
    loadDataChunk,
  ]);

  // Check if we have all required data and trigger loading if needed
  // Use ref to prevent multiple triggers
  const loadingRef = useRef(false);

  useEffect(() => {
    // Wait for preferences to be loaded before fetching data
    if (!preferencesLoaded) {
      return;
    }

    // Calculate the range of API pages we need
    const firstPageNeeded = Math.floor(lazyState.first / apiPageSize) + 1;
    const lastRecordIndex = lazyState.first + apiPageSize - 1;
    const lastPageNeeded = Math.floor(lastRecordIndex / apiPageSize) + 1;

    // Calculate total possible pages based on totalRecords
    const totalPages = Math.ceil(totalRecords / apiPageSize);

    // Adjust lastPageNeeded to not exceed totalPages if we have valid totalRecords
    const adjustedLastPageNeeded =
      totalRecords > 0 && totalPages > 0 ? Math.min(lastPageNeeded, totalPages) : lastPageNeeded;

    // Check if we have all required pages cached
    let hasAllPages = true;
    for (let page = firstPageNeeded; page <= adjustedLastPageNeeded; page++) {
      if (!dataCache[page]) {
        hasAllPages = false;
        break;
      }
    }

    // If we don't have all pages and we're not already loading, trigger loading
    if (!hasAllPages && !loadingRef.current) {
      loadingRef.current = true;
      loadLazyData().finally(() => {
        // Reset loading ref after loading is complete
        loadingRef.current = false;
      });
    }
  }, [lazyState.first, dataCache, totalRecords, preferencesLoaded, apiPageSize, loadLazyData]);

  // Cleanup cache
  useEffect(() => {
    return () => {
      setDataCache({});
      setTotalRecords(0);
    };
  }, []);

  const onPage = async (event: LazyStateEvent) => {
    // Check if rowsPerPage changed
    if (event.rows !== lazyState.rows) {
      // Update user preference on backend
      try {
        await updateRecordsPerPage(event.rows);
        if (mountedRef.current) {
          setApiPageSize(event.rows);
        }
      } catch (error) {
        console.warn("Failed to update recordsPerPage preference:", error);
        // Continue anyway with local change
        if (mountedRef.current) {
          setApiPageSize(event.rows);
        }
      }

      // Clear cache when rowsPerPage changes to reload data
      setDataCache({});
      setTotalRecords(0);
      // Only take pagination fields — PrimeReact's lazy event also injects the
      // DataTable's own filter/sort state (null when uncontrolled) which would
      // overwrite the filter values managed by this hook.
      setLazyState(prev => ({
        ...prev,
        first: event.first,
        rows: event.rows,
        page: (event as { page?: number }).page,
      }));
      return;
    }

    // Calculate current page number based on 'first' index and rows per page
    const currentPage = Math.floor(event.first / event.rows) + 1;

    // Calculate total pages based on totalRecords and rows per page
    const totalPages = Math.ceil(totalRecords / event.rows);

    // If we have valid totalRecords and the requested page exceeds total pages,
    // adjust to the last valid page
    if (totalRecords > 0 && totalPages > 0 && currentPage > totalPages) {
      // Adjust to last valid page
      const lastPageFirst = (totalPages - 1) * event.rows;
      setLazyState(prev => ({
        ...prev,
        first: lastPageFirst,
        rows: event.rows,
        page: totalPages,
      }));
    } else {
      setLazyState(prev => ({
        ...prev,
        first: event.first,
        rows: event.rows,
        page: (event as { page?: number }).page,
      }));
    }
  };

  const onSort = (event: LazyStateEvent) => {
    // Reset to first page when sorting changes
    // Only take sort fields — same reason as onPage: PrimeReact injects null
    // filter state that would overwrite the hook-managed filters.
    setDataCache({});
    setTotalRecords(0);
    setLazyState(prev => ({
      ...prev,
      first: 0,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  const onFilter = (event: LazyStateEvent) => {
    event.first = 0; // Reset to first page
    // Clear cache
    setDataCache({});
    // Reset totalRecords
    setTotalRecords(0);
    setLazyState(event);
  };

  // Get visible data
  const visibleData = getVisibleData();

  // Determine if we're in a loading state (data contains null placeholders)
  const isLoading =
    loading || !preferencesLoaded || (visibleData.length > 0 && visibleData[0] === null);

  // Return the pagination state and functions
  return {
    // Data
    data: visibleData,
    // Include preferences loading and placeholder detection in the loading state
    loading: isLoading,
    totalRecords,

    // State
    lazyState,
    setLazyState,

    // Functions
    loadDataChunk,
    setDataCache,
    setTotalRecords,
    onPage,
    onSort,
    onFilter,

    // API page size (synced with user preferences)
    apiPageSize,

    // Cache management
    dataCache,
  };
};

export default useApiPagination;
